import { useContext, useMemo, useState } from "react";
import { ExternalLink, Table2, Trash } from "lucide-react";
import { Switch } from "@/components/shadcn/switch";
import { BetaFieldContext } from "@/components/Form/NoxusFormLabel";
import { ReleaseStatusBadge } from "@/components/UI/ReleaseStatusBadge";
import { Skeleton } from "@/components/shadcn/skeleton";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import type { RichTextValue } from "@/components/Form/RichTextField";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";
import { useDisclosure } from "@/utils/useDisclosure";
import type { TableWithCount } from "@/generated";
import { AddNewEntry } from "./AssistantDetailsContent";
import { AssistantTablePicker } from "./AssistantTablePicker";
import { ExpandableTextarea } from "./ExpandableTextarea";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { NoxusMenu } from "../UI/NoxusMenu";
import { cn } from "@/lib/utils";
import { UnavailableResourceBadge } from "./UnavailableResourceBadge";

const CONTEXT_FIELD_CONFIG: NoxusFormEntryConfig = {
  type: "str",
  display: {
    type: "rich_text_variables_ai",
    label: "Table context",
    connector_name: "",
    enable_heuristics: true,
    heuristic_context:
      "Context for a workspace table an agent can query: what it holds and when the agent should query it.",
    heuristic_types: ["clarity", "specificity", "actionability"],
  },
};

export type AgentTablesEntry = {
  table_id: string;
  // The picker edits context as plain text; the row editor uses the rich-text
  // widget. Both are accepted — the backend coerces either into TipTapContent.
  context?: RichTextValue | string | null;
  enabled?: boolean;
  read_only?: boolean;
  max_rows?: number | null;
  query_timeout_seconds?: number | null;
};

function useCustomTables() {
  const { spotGroup } = useSpotUser();
  const isReadOnly = useEditorReadOnly();
  const result = useRemoteData(
    DATA_KEYS.TABLES,
    { path: { group_id: spotGroup.id } },
    { enabled: !isReadOnly }
  );
  const data = result.data as TableWithCount[] | undefined;
  const tables = useMemo(
    () => (data ?? []).filter((t) => (t.source ?? "custom") === "custom"),
    [data]
  );
  return {
    tables,
    isLoading: result.isLoading,
    catalogLoaded: !isReadOnly && result.isSuccess,
  };
}

const TableRow = ({
  entry,
  table,
  isMissing,
  isDisabled,
  onChange,
  onRemove,
}: {
  entry: AgentTablesEntry;
  table: TableWithCount | undefined;
  isMissing?: boolean;
  isDisabled?: boolean;
  onChange: (next: AgentTablesEntry) => void;
  onRemove: () => void;
}) => {
  const enabled = entry.enabled ?? true;
  const name = table?.name ?? (isMissing ? "Deleted table" : "Table");
  const [editOpen, setEditOpen] = useState(false);
  return (
    <>
      <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
        <div className="flex items-center justify-between gap-2 p-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-muted-foreground">
              <Table2 className="size-4" />
            </div>
            <p
              className={cn(
                "text-sm font-medium truncate min-w-0",
                isMissing ? "text-muted-foreground" : "flex-1"
              )}
            >
              {name}
            </p>
            {isMissing && <UnavailableResourceBadge kind="table" />}
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={enabled}
              disabled={isDisabled || isMissing}
              onCheckedChange={(checked) =>
                onChange({ ...entry, enabled: checked })
              }
            />
            <NoxusMenu
              isSmall
              isDisabled={isDisabled}
              itemsArray={[
                {
                  label: "Edit configuration",
                  icon: ExternalLink,
                  onClick: () => setEditOpen(true),
                  isHidden: isMissing,
                },
                {
                  label: "Remove",
                  icon: Trash,
                  onClick: onRemove,
                  color: "destructive",
                },
              ]}
            />
          </div>
        </div>
        {enabled && !isMissing && (
          <ExpandableTextarea
            value={entry.context ?? ""}
            onChange={(value) => onChange({ ...entry, context: value })}
            isDisabled={isDisabled}
            label="Table context"
            placeholder="What does this table hold? When should the agent query it?"
            fieldConfig={CONTEXT_FIELD_CONFIG}
          />
        )}
      </div>
      {editOpen && (
        <AssistantTablePicker
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          onAdd={(updated) => onChange(updated)}
          filteredTableIds={[]}
          editingEntry={entry}
          editingTableName={name}
        />
      )}
    </>
  );
};

export const TablesField = ({
  tables,
  onTablesChange,
  isDisabled,
}: {
  tables: AgentTablesEntry[] | undefined;
  onTablesChange: (tables: AgentTablesEntry[]) => void;
  isDisabled?: boolean;
}) => {
  const { tables: customTables, isLoading, catalogLoaded } = useCustomTables();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const byId = useMemo(() => {
    const m = new Map<string, TableWithCount>();
    for (const t of customTables) m.set(t.id, t);
    return m;
  }, [customTables]);

  const list = tables ?? [];
  const usedIds = new Set(list.map((e) => e.table_id));
  const available = customTables.filter((t) => !usedIds.has(t.id));

  const update = (next: AgentTablesEntry[]) => {
    if (!isDisabled) onTablesChange(next);
  };

  const isBeta = useContext(BetaFieldContext);

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-foreground leading-none">
            Tables
          </p>
          {isBeta && <ReleaseStatusBadge status="beta" className="shrink-0" />}
        </div>
        <p className="text-xs font-regular text-muted-foreground mt-1.5">
          Pick the workspace tables this agent can use. Add context for each so
          the agent knows what it holds and when to use it.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : list.length === 0 && customTables.length === 0 ? (
        <NoxusAlert
          icon={Table2}
          description="No custom tables in this workspace."
          action={{
            label: (
              <>
                <ExternalLink />
                Open tables
              </>
            ),
            onClick: () => window.open("/?tab=tables", "_blank", "noopener"),
          }}
        />
      ) : (
        <>
          {list.map((entry, idx) => {
            const key = entry.table_id || idx;
            const table = byId.get(entry.table_id);
            return (
              <TableRow
                key={key}
                entry={entry}
                table={table}
                isMissing={!table && catalogLoaded}
                isDisabled={isDisabled}
                onChange={(next) =>
                  update(list.map((e, i) => (i === idx ? next : e)))
                }
                onRemove={() => update(list.filter((_, i) => i !== idx))}
              />
            );
          })}

          <AddNewEntry
            label="Add table"
            disabledLabel={
              available.length === 0 ? "No more tables to add" : undefined
            }
            onClick={onOpen}
            isDisabled={isDisabled || available.length === 0}
          />
        </>
      )}
      {isOpen && (
        <AssistantTablePicker
          isOpen={isOpen}
          onClose={onClose}
          onAdd={(entry) => update([...list, entry])}
          filteredTableIds={list.map((e) => e.table_id)}
        />
      )}
    </div>
  );
};
